<template>
	<v-row class="full-height">
		<v-col cols="12" class="flex-none grid-header">
			<div class="settings-header-actions">
				<v-btn
					depressed
					rounded
					color="success"
					@click="redirectToEzeepPortal()"
					:disabled="activeEdit !== null"
				>
					<v-icon left small>mdi-open-in-new</v-icon>
					Manage in ezeep
				</v-btn>
				<v-btn
					depressed
					rounded
					color="primary"
					@click="refreshPrinters(true)"
					:disabled="loading"
					class="ml-2"
				>
					<v-icon left>mdi-sync</v-icon>
					Sync Now
				</v-btn>
				<v-btn
					depressed
					rounded
					outlined
					@click="showReorderDialog()"
					:disabled="loading || printers.length < 2"
					class="ml-2"
				>
					<v-icon left>mdi-sort</v-icon>
					Reorder
				</v-btn>
			</div>
		</v-col>

		<v-col cols="12" class="flex-auto printers-content">
			<v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

			<!-- Sync status bar -->
			<div v-if="lastSyncedAt && !loading" class="sync-info-bar mb-2">
				<v-icon :color="syncStatusColor" class="mr-1">mdi-cloud-check</v-icon>
				<span>
					Synced {{ formatSyncTime(lastSyncedAt) }}
					<span class="grey--text ml-2">(auto-refresh every 60s)</span>
				</span>
			</div>

			<div v-if="error" class="error-message mb-4">
				<v-alert type="error" dense>
					{{ error }}
				</v-alert>
			</div>

			<div class="table settings-table">
				<div class="table-container">
					<table>
						<thead>
							<tr>
								<th style="width: 5%">Order</th>
								<th style="width: 22%">Printer Name</th>
								<th style="width: 17%">Model</th>
								<th style="width: 13%">Form Code</th>
								<th style="width: 13%">Location</th>
								<th style="width: 8%" class="text-center">Status</th>
								<th style="width: 8%" class="text-center">Monitor</th>
								<th style="width: 8%" class="text-center">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(printer, i) in sortedPrinters" :key="printer.id || i">
								<template v-if="activeEdit && printer.id === activeEdit.id">
									<td>
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model.number="activeEdit.sequence"
												type="number"
												min="1"
											></v-text-field>
										</div>
									</td>
									<td>
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="activeEdit.name"
											></v-text-field>
										</div>
									</td>
									<td>
										<div class="form-input">
											<v-text-field
												outlined
												dense
												disabled
												hide-details="auto"
												v-model="activeEdit.modelName"
											></v-text-field>
										</div>
									</td>
									<td>
										<div class="form-input">
											<v-select
												v-model="activeEdit.formCode"
												:items="printerTypes"
												dense
												outlined
												hide-details="auto"
											></v-select>
										</div>
									</td>
									<td>
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="activeEdit.location"
											></v-text-field>
										</div>
									</td>
									<td class="text-center">
										<v-chip :color="activeEdit.status === 'Online' ? 'success' : 'error'">
											{{ activeEdit.status || "Offline" }}
										</v-chip>
									</td>
									<td class="text-center">
										<v-switch
											v-model="activeEdit.monitorHealth"
											dense
											hide-details
											class="mt-0 pt-0 d-inline-flex"
										></v-switch>
									</td>
									<td class="text-center">
										<v-btn icon color="error" @click="cancelEdit()" class="mr-2">
											<v-icon>mdi-close</v-icon>
										</v-btn>
										<v-btn icon color="success" @click="savePrinter()">
											<v-icon>mdi-check</v-icon>
										</v-btn>
									</td>
								</template>
								<template v-else>
									<td>{{ printer.sequence || i + 1 }}</td>
									<td>{{ printer.name }}</td>
									<td>{{ printer.modelName }}</td>
									<td>{{ printer.formCode }}</td>
									<td>{{ printer.location }}</td>
									<td class="text-center">
										<v-chip
											x-small
											:color="getStatusColor(printer)"
										>
											{{ printer.status || "Unknown" }}
										</v-chip>
									</td>
									<td class="text-center">
										<v-icon small :color="printer.monitorHealth ? 'success' : 'grey lighten-1'">
											{{ printer.monitorHealth ? 'mdi-shield-check' : 'mdi-shield-off-outline' }}
										</v-icon>
									</td>
									<td class="text-center">
										<v-btn icon color="primary" @click="editPrinter(printer)">
											<v-icon>mdi-pencil</v-icon>
										</v-btn>
									</td>
								</template>
							</tr>
							<tr v-if="sortedPrinters.length === 0 && !loading">
								<td colspan="8" class="text-center py-4">
									No printers available. Enable printers in the ezeep portal and click "Sync Now".
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>

			<!-- Auto-Print Configuration Section -->
			<div class="auto-print-section mt-6">
				<h3 class="section-title mb-3">
					<v-icon left>mdi-printer-settings</v-icon>
					Auto-Print Configuration
				</h3>
				<p class="section-description grey--text mb-3">
					These are the system-wide default settings for automatic printing. When enabled, documents will print automatically to the selected printer. Individual users can override these defaults from their Account Settings page.
				</p>

				<v-progress-linear v-if="loadingConfigurations" indeterminate color="primary" class="mb-2"></v-progress-linear>

				<div class="table settings-table" v-if="!loadingConfigurations">
					<div class="table-container">
						<table>
							<thead>
								<tr>
									<th style="width: 40%">Document Type</th>
									<th style="width: 40%">Printer</th>
									<th style="width: 20%" class="text-center">Enabled</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="config in printConfigurations" :key="config.documentType">
									<td>
										<div class="d-flex align-center">
											<v-icon class="mr-2" color="primary">{{ config.documentTypeIcon }}</v-icon>
											<div>
												<div class="font-weight-medium">{{ config.documentTypeName }}</div>
												<div class="text-caption grey--text">{{ config.documentTypeDescription }}</div>
											</div>
										</div>
									</td>
									<td>
										<v-select
											v-model="config.printerId"
											:items="printerSelectOptions"
											item-text="name"
											item-value="id"
											dense
											outlined
											clearable
											placeholder="Select printer..."
											:disabled="!config.isEnabled"
											:error="!!configValidationErrors[config.documentType]"
											:error-messages="configValidationErrors[config.documentType]"
											:hide-details="!configValidationErrors[config.documentType]"
											@change="clearConfigValidationError(config.documentType)"
										></v-select>
									</td>
									<td class="text-center">
										<v-switch
											v-model="config.isEnabled"
											color="success"
											hide-details
											class="mt-0 d-inline-flex"
										></v-switch>
									</td>
								</tr>
								<tr v-if="printConfigurations.length === 0 && !loadingConfigurations">
									<td colspan="3" class="text-center py-4">
										No print configurations available.
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>

				<div class="d-flex justify-end mt-3">
					<v-btn
						color="primary"
						depressed
						:loading="savingConfigurations"
						:disabled="loadingConfigurations || !hasConfigurationChanges"
						@click="savePrintConfigurations"
					>
						<v-icon left>mdi-content-save</v-icon>
						Save Configuration
					</v-btn>
				</div>
			</div>
		</v-col>

		<!-- Reorder Dialog -->
		<v-dialog v-model="reorderDialog" max-width="500px">
			<v-card>
				<v-card-title>Reorder Printers</v-card-title>
				<v-card-text>
					<p class="mb-4">Drag and drop printers to change their order.</p>

					<v-list>
						<draggable v-model="reorderPrinters" handle=".handle" @end="onDragEnd">
							<v-list-item
								v-for="(printer, i) in reorderPrinters"
								:key="printer.id || i"
								class="mb-2 grey lighten-4"
							>
								<v-list-item-icon class="handle mr-2 cursor-move">
									<v-icon>mdi-drag</v-icon>
								</v-list-item-icon>

								<v-list-item-content>
									<v-list-item-title>
										{{ printer.name }}
										<v-chip x-small color="primary" class="ml-1" v-if="printer.isEzeepPrinter"
											>ezeep</v-chip
										>
									</v-list-item-title>
									<v-list-item-subtitle>{{
										printer.description || printer.location
									}}</v-list-item-subtitle>
								</v-list-item-content>

								<v-list-item-action>
									<v-icon v-if="printer.isDefault" color="success">mdi-check</v-icon>
								</v-list-item-action>
							</v-list-item>
						</draggable>
					</v-list>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn color="error" text @click="cancelReorder">Cancel</v-btn>
					<v-btn color="success" @click="saveReorder">Save Order</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-row>
</template>

<script>
import draggable from "vuedraggable";

export default {
	components: {
		draggable,
	},
	name: "Printers",
	data() {
		return {
			printers: [],
			ezeepPrinters: [],
			printerTypes: [
				"Wide Carriage",
				"Narrow Carriage",
				"Lazer / Inkjet",
				"Color Lazer / Inkjet",
				"Fax / Lazer / Inkjet",
				"Scanner / Fax / Lazer / Inkjet",
				"High Speed / Vol",
				"Band Printer",
				"Label Printer",
				"Invoice",
				"Report",
				"Photo",
			],
			activeEdit: null,
			loading: false,
			error: null,
			manualFormValid: false,

			// ezeep printers
			loadingEzeepPrinters: false,
			ezeepError: null,
			selectedEzeepPrinter: null,

			// Reorder dialog
			reorderDialog: false,
			reorderPrinters: [],

			// Sync status tracking
			lastSyncedAt: null,
			nextSyncAt: null,

			// Polling for auto-refresh
			pollingInterval: null,
			pollingEnabled: true,
			pollingIntervalMs: 60000, // 60 seconds

			// Auto-print configuration
			printConfigurations: [],
			originalConfigurations: [],
			loadingConfigurations: false,
			savingConfigurations: false,
			configValidationErrors: {}, // { documentType: "error message" }
		};
	},
	computed: {
		sortedPrinters() {
			return [...this.printers].sort((a, b) => {
				// Sort by sequence first
				if (a.sequence && b.sequence) {
					return a.sequence - b.sequence;
				}
				// If one has sequence and the other doesn't, prioritize the one with sequence
				if (a.sequence && !b.sequence) return -1;
				if (!a.sequence && b.sequence) return 1;
				// If neither has sequence, sort by name
				return a.name.localeCompare(b.name);
			});
		},
		/**
		 * Computed color for sync status indicator
		 */
		syncStatusColor() {
			if (!this.lastSyncedAt) return "grey";
			const minutesSinceSync = this.getMinutesSinceSync(this.lastSyncedAt);
			if (minutesSinceSync <= 2) return "success";
			if (minutesSinceSync <= 15) return "primary";
			return "warning";
		},
		/**
		 * Options for printer select dropdown in auto-print configuration
		 */
		printerSelectOptions() {
			return this.sortedPrinters.map((printer) => ({
				id: printer.printerId,
				name: printer.name,
			}));
		},
		/**
		 * Check if there are unsaved changes in print configuration
		 */
		hasConfigurationChanges() {
			if (this.printConfigurations.length !== this.originalConfigurations.length) {
				return true;
			}
			return this.printConfigurations.some((config, index) => {
				const original = this.originalConfigurations[index];
				if (!original) return true;
				return (
					config.printerId !== original.printerId ||
					config.isEnabled !== original.isEnabled
				);
			});
		},
	},
	created() {
		this.refreshPrinters();
		this.loadPrintConfigurations();
	},
	mounted() {
		// Start auto-polling when component is mounted
		this.startPolling();

		// Handle visibility change to pause/resume polling
		document.addEventListener("visibilitychange", this.handleVisibilityChange);
	},
	beforeDestroy() {
		// Clean up polling interval when component is destroyed
		this.stopPolling();
		document.removeEventListener("visibilitychange", this.handleVisibilityChange);
	},
	methods: {
		async refreshPrinters(forceSync = false) {
			this.loading = true;
			this.error = null;

			try {
				// Added sync parameter to force synchronization with Ezeep API
				const url = `/api/print/printers${forceSync ? "?sync=true" : ""}`;
				const response = await this.$http.get(url);

				// Handle new response structure with printers array and sync metadata
				const responseData = response.data;
				const printersData = responseData.printers || responseData;

				// Update sync metadata
				this.lastSyncedAt = responseData.lastSyncedAt || null;
				this.nextSyncAt = responseData.nextSyncAt || null;

				this.printers = printersData.map((printer) => ({
					...printer,
					id: printer.id,
					isEzeepPrinter: true,
					description: printer.description || "",
					modelName: printer.modelName || printer.model_name || "",
					formCode: printer.formCode || this.determineFormCode(printer),
					sequence: printer.sequence || null,
					lastSyncedAt: printer.lastSyncedAt || null,
					statusChangedAt: printer.statusChangedAt || null,
					lastOnlineAt: printer.lastOnlineAt || null,
					consecutiveFailures: printer.consecutiveFailures || 0,
					monitorHealth: printer.monitorHealth !== false,
				}));
				console.log("Printers loaded:", this.printers, "Last synced:", this.lastSyncedAt);
			} catch (error) {
				console.error("Error fetching printers:", error);
				this.error = "Failed to load printers. Please try again.";
			} finally {
				this.loading = false;
			}
		},

		determineFormCode(printer) {
			// Logic to determine form code based on printer capabilities
			if (printer.supportedFormats && printer.supportedFormats.includes("PDF")) {
				return "Lazer / Inkjet";
			}
			if (printer.name && printer.name.toLowerCase().includes("label")) {
				return "Label Printer";
			}
			return "Lazer / Inkjet"; // Default
		},

		redirectToEzeepPortal() {
			window.open("https://app.ezeep.com/printers", "_blank");
		},

		// Polling methods for auto-refresh
		startPolling() {
			if (this.pollingInterval) {
				clearInterval(this.pollingInterval);
			}
			if (this.pollingEnabled) {
				this.pollingInterval = setInterval(() => {
					if (!this.loading && !this.activeEdit) {
						console.log("Auto-refreshing printers...");
						this.refreshPrinters(false);
					}
				}, this.pollingIntervalMs);
			}
		},

		stopPolling() {
			if (this.pollingInterval) {
				clearInterval(this.pollingInterval);
				this.pollingInterval = null;
			}
		},

		handleVisibilityChange() {
			if (document.hidden) {
				// Page is hidden, pause polling
				this.stopPolling();
			} else {
				// Page is visible again, refresh and resume polling
				this.refreshPrinters(false);
				this.startPolling();
			}
		},

		// Status and time formatting methods
		getMinutesSinceSync(dateString) {
			if (!dateString) return Infinity;
			const syncDate = new Date(dateString);
			const now = new Date();
			return Math.floor((now - syncDate) / 60000);
		},

		formatSyncTime(dateString) {
			if (!dateString) return "Never";
			const date = new Date(dateString);
			const now = new Date();
			const diffMs = now - date;
			const diffMins = Math.floor(diffMs / 60000);
			const diffHours = Math.floor(diffMs / 3600000);

			if (diffMins < 1) return "Just now";
			if (diffMins < 60) return `${diffMins} min ago`;
			if (diffHours < 24) return `${diffHours} hours ago`;
			return date.toLocaleString();
		},

		/**
		 * Get status color based on printer status
		 */
		getStatusColor(printer) {
			if (!printer.status) return "grey"; // Unknown

			switch (printer.status) {
				case "Online":
					return "success";
				case "Offline":
					return "error";
				case "Unavailable":
					return "grey"; // Toggled off in ezeep
				case "Improperly Configured":
					return "warning";
				default:
					return "grey";
			}
		},

		getNextSequence() {
			if (this.printers.length === 0) return 1;

			const maxSequence = Math.max(
				...this.printers.filter((p) => p.sequence).map((p) => p.sequence)
			);

			return isFinite(maxSequence) ? maxSequence + 1 : 1;
		},

		async fetchEzeepPrinters() {
			this.loadingEzeepPrinters = true;
			this.ezeepError = null;

			try {
				// Use the non-syncing version here since this is just for displaying available printers
				const response = await this.$http.get(`/api/print/printers`);

				// Handle new response structure
				const responseData = response.data;
				const printersData = responseData.printers || responseData;

				// Filter out printers that are already added
				const existingIds = this.printers.map((p) => p.id);
				this.ezeepPrinters = printersData.filter((p) => !existingIds.includes(p.id));
			} catch (error) {
				console.error("Error fetching ezeep printers:", error);
				this.ezeepError = "Failed to load ezeep printers. Please try again.";
			} finally {
				this.loadingEzeepPrinters = false;
			}
		},

		selectEzeepPrinter(printer) {
			this.selectedEzeepPrinter = printer;
		},

		editPrinter(printer) {
			// Create a copy to edit
			const printerToEdit = { ...printer };

			// Ensure modelName is set correctly
			if (!printerToEdit.modelName && printerToEdit.model_name) {
				printerToEdit.modelName = printerToEdit.model_name;
			}

			console.log("Editing printer:", printerToEdit);
			this.activeEdit = printerToEdit;
		},

		cancelEdit() {
			this.activeEdit = null;
		},

		async savePrinter() {
			if (!this.activeEdit) return;

			try {
				// Create a copy of the edited printer and convert back to API format
				const updatedPrinter = { ...this.activeEdit };

				// Map modelName back to model_name for API
				if (updatedPrinter.modelName) {
					updatedPrinter.model_name = updatedPrinter.modelName;
				}

				console.log("Saving printer update:", updatedPrinter);

				// Use PUT endpoint to update a single printer
				const response = await this.$http.put(
					`/api/print/printers/${updatedPrinter.id}`,
					updatedPrinter
				);

				console.log("Printer update response:", response);

				// Find the index of the printer in our array
				const index = this.printers.findIndex((p) => p.id === updatedPrinter.id);

				if (index !== -1) {
					// Update the local copy
					this.$set(this.printers, index, updatedPrinter);
				}

				// Exit edit mode
				this.activeEdit = null;
			} catch (error) {
				console.error("Error updating printer:", error);
				this.error = "Failed to update printer. Please try again.";
			}
		},

		updateDefaultPrinter(newDefaultId = null) {
			// If a printer was set as default, ensure only one printer is default
			this.printers.forEach((printer) => {
				if (newDefaultId) {
					// If we have a specific new default, only that one should be default
					printer.isDefault = printer.id === newDefaultId;
				} else if (printer.isDefault) {
					// If we don't have a specific new default, find the first one marked as default
					// and ensure all others are not default
					newDefaultId = printer.id;
				}
			});

			// If we found a default printer, ensure all others are not default
			if (newDefaultId) {
				this.printers.forEach((printer) => {
					if (printer.id !== newDefaultId) {
						printer.isDefault = false;
					}
				});
			}
		},

		async saveChanges() {
			try {
				console.log("Saving printer changes:", this.printers);
				await this.$http.post(`/api/print/printers`, this.printers);
				console.log("Printer changes saved successfully");
			} catch (error) {
				console.error("Error saving printers:", error);
				this.error = "Failed to save printer changes. Please try again.";
			}
		},

		// Reorder methods
		showReorderDialog() {
			// Create a copy of the sorted printers for reordering
			this.reorderPrinters = [...this.sortedPrinters];
			this.reorderDialog = true;
		},

		cancelReorder() {
			this.reorderDialog = false;
			this.reorderPrinters = [];
		},

		async saveReorder() {
			// Update the sequence numbers based on the new order
			this.reorderPrinters.forEach((printer, index) => {
				printer.sequence = index + 1;

				// Find the corresponding printer in the original array and update its sequence
				const originalPrinter = this.printers.find((p) => p.id === printer.id);

				if (originalPrinter) {
					originalPrinter.sequence = printer.sequence;
				}
			});

			// Save the changes
			await this.saveChanges();

			// Close the dialog
			this.reorderDialog = false;
		},

		onDragEnd() {
			// This method is called when a drag operation ends
			// We don't need to do anything here, but it's useful for debugging
			console.log(
				"Drag ended, new order:",
				this.reorderPrinters.map((p) => p.name)
			);
		},

		// Auto-print configuration methods
		async loadPrintConfigurations() {
			this.loadingConfigurations = true;
			try {
				const response = await this.$http.get("/api/print/configuration");
				this.printConfigurations = response.data.map((config) => ({
					...config,
					printerId: config.printerId || null,
					isEnabled: config.isEnabled || false,
				}));
				// Store a deep copy for change detection
				this.originalConfigurations = JSON.parse(JSON.stringify(this.printConfigurations));
				console.log("Print configurations loaded:", this.printConfigurations);
			} catch (error) {
				console.error("Error loading print configurations:", error);
				this.error = "Failed to load print configurations.";
			} finally {
				this.loadingConfigurations = false;
			}
		},

		clearConfigValidationError(documentType) {
			if (this.configValidationErrors[documentType]) {
				this.$delete(this.configValidationErrors, documentType);
			}
		},

		validatePrintConfigurations() {
			this.configValidationErrors = {};
			let isValid = true;

			this.printConfigurations.forEach((config) => {
				if (config.isEnabled && !config.printerId) {
					this.$set(this.configValidationErrors, config.documentType, "Select a printer");
					isValid = false;
				}
			});

			return isValid;
		},

		async savePrintConfigurations() {
			// Validate before saving
			if (!this.validatePrintConfigurations()) {
				return;
			}

			this.savingConfigurations = true;
			try {
				const configurationsToSave = this.printConfigurations.map((config) => ({
					documentType: config.documentType,
					printerId: config.printerId,
					isEnabled: config.isEnabled,
				}));

				await this.$http.post("/api/print/configuration", configurationsToSave);

				// Update original configurations after successful save
				this.originalConfigurations = JSON.parse(JSON.stringify(this.printConfigurations));

				// Show success message
				this.$toast.success("Print configuration saved successfully");
			} catch (error) {
				console.error("Error saving print configurations:", error);
				const errorMessage =
					error.response?.data?.message ||
					error.response?.data ||
					"Failed to save print configurations.";
				this.$toast.error(errorMessage);
			} finally {
				this.savingConfigurations = false;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
// Make content area scrollable
.printers-content {
	overflow-y: auto;
	padding-bottom: 2rem;

	// Override global table styles so tables don't fill all space
	.table {
		height: auto;
		min-height: auto;

		&-container {
			position: relative;
			top: auto;
			bottom: auto;
			left: auto;
			right: auto;
		}
	}
}

.settings {
	&-header {
		&-actions {
			grid-column: 3;
			margin-left: auto;
			display: flex;
			align-items: center;

			.v-btn {
				min-width: 13rem;
				text-transform: initial;

				&:first-child {
					background-color: $color-white;
					border-color: $color-gray;
				}
			}
		}
	}
}

.selected-printer {
	background-color: rgba(0, 0, 0, 0.05);
}

.cursor-move {
	cursor: move;
}

.handle {
	cursor: grab;
}

// Sync info bar
.sync-info-bar {
	display: flex;
	align-items: center;
	padding: 8px 0;
	font-size: 1.4rem;
	color: #555;
}

// Auto-print configuration section
.auto-print-section {
	border-top: 1px solid #e0e0e0;
	padding-top: 1.5rem;

	.section-title {
		display: flex;
		align-items: center;
		font-size: 1.1rem;
		font-weight: 500;
		color: #333;
	}

	.section-description {
		font-size: 1.4rem;
	}

	// Make the table more compact for config section
	table {
		td {
			padding: 12px 8px;
			vertical-align: middle;
		}
	}
}
</style>
